import FolderIcon from "@mui/icons-material/Folder";
import { Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteSweep } from "@mui/icons-material";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useProjects } from "../Behaviors/projects";
import { Project } from "../api";
import { useStore } from "../State/zustandStore";
import { getProjectIdentifier } from "../Behaviors/projectTools";
import { DeleteCompleted } from "./DeleteCompleted";

export interface props {
  name: string;
  projects: Project[]
}

export interface deleteProps {
  project: Project
}

const DeleteAll = (props: {show: () => void}) => {
    return (
        <Tooltip title="Hold Shift and click to delete Completed jobs and their files">
            <DeleteSweep
                className="icon-button-alt"
                onMouseDown={(event) => event.preventDefault()}
                onClick={ async (event) => {
                    if (event.shiftKey){
                        props.show();
                    }}}
            />
        </Tooltip>
    );
};

const Delete = (props: deleteProps) => {
    const projects = useStore().projects;
    const projectId = projects.selectedProjectId;
    const { deleteProject } = useProjects(projectId!);
    return (
        <Tooltip title="Hold shift and click to delete this job and all of its files">
            <DeleteIcon
                className="icon-button-alt"
                onMouseDown={(event) => event.preventDefault()}
                onClick={ async (event) => {
                    if (event.shiftKey && !projects.busy.has(projectId!)){
                        event.stopPropagation(); // Prevent project from being selected
                        await deleteProject(props.project);
                    }}}
            />
        </Tooltip>
    );
};

export const Folder = (props: props) => {
    const { projects } = useStore();
    const queryClient = useQueryClient();
    const projectId = projects.selectedProjectId;
    const [showDeleteCompleted, setShowDeleteCompleted] = useState(false);
    const { batchDelete } = useProjects(projectId);

    const truncateString = (str: string, maxLength: number) => {
        if (str.length <= maxLength) {
            return str;
        }
        return `${str.substring(0, maxLength - 3)}...`;
    };

    const handleDelete = async (selected: string) => {
        let filterDate = Date.now();

        switch (selected) {
        case "week":
            filterDate = Date.now() - 60 * 60 * 24 * 7 * 1000; // 7 days ago
            break;
        case "month":
            filterDate = Date.now() - 60 * 60 * 24 * 30 * 1000; // 30 days ago
            break;
        }

        const projectIds = props.projects.filter(p => new Date(p.lastModified ?? 0) < new Date(filterDate)).map(p => p.projectId!);

        setShowDeleteCompleted(false);
        await batchDelete(projectIds);
    };

    const renderProjectPopover = (project: Project) => {

        let title = project.releaseInfo?.title ?? project.resourceInfo?.title ?? "";
        let version = project.releaseInfo?.version ?? project.resourceInfo?.version;

        if (version){
            if (version.length > 14){
                version = truncateString(version, 14);
            }

            version = `(${version})`;
            title = `${truncateString(title, 38 - version.length)} ${version}`;
        }
        else {
            title = truncateString(title, 39);
        }
        return (

            <Popover id="popover-basic">
                {<Popover.Body>
                    <div className="popover-wrapper">
                        <div>{getProjectIdentifier(project) ?? "No UPC Selected"}</div>
                        <div>{project.releaseInfo?.artists ?? project.resourceInfo?.artists ?? " "}</div>
                        <div> {title}</div>
                        <div> {project.releaseInfo?.parentalAdvisory ?? project.resourceInfo?.parentalAdvisory ?? " "}</div>
                        <div> {project.releaseInfo?.configuration ?? project.resourceInfo?.resourceType ?? " "}</div>
                        <div> {project.releaseInfo?.resolutionType ?? project.resourceInfo?.channelConfig ?? " "}</div>
                    </div>
                </Popover.Body>}
            </Popover>
        );
    };
    return (
        <>
            <tr key={props.name}>
                <td>
                    <FolderIcon />
                </td>
                <td>
                    {`${props.name} (${props.projects.length})`}
                </td>
                <td>
                    {props.name === "Completed" && props.projects.length > 0 && <DeleteAll show={() => setShowDeleteCompleted(true)} />}
                </td>
                <td />
            </tr>
            {props.projects.map(project => {
                const isSelected = project.projectId === projectId;
                const hideTrashcan = props.name === "Uploading" || props.name === "In Progress";
                return (
                    <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="right"
                        overlay={renderProjectPopover(project)}
                        rootClose
                        key={project.projectId!}
                    >
                        <tr key={project.projectId!} className={ `project ${isSelected ? "selected " : ""}`} onClick={async () => {
                            projects.setSelectedProjectId(project.projectId!);
                            queryClient.invalidateQueries({ queryKey: ["project", project.projectId]});

                            const upcOrIsrc = project?.releaseInfo?.upc ?? project?.resourceInfo?.isrc;
                            const productIdentifier = (upcOrIsrc)
                                ? upcOrIsrc
                                : "No product selected";

                            document.title = `AIT: ${productIdentifier}`;
                        }}>
                            <td />
                            <td className={"project-name text-break"} > {project?.name ?? "New Draft"}  </td>
                            <td className={"shift-hidden"}>{project.projectId}</td>
                            <td>
                                {
                                    hideTrashcan
                                        ? <></>
                                        : <Delete project={project} />
                                }
                            </td>
                        </tr>
                    </OverlayTrigger>
                );
            })
            }
            <DeleteCompleted key={"DeleteCompleted"} show={showDeleteCompleted} onDelete={handleDelete} onHide={() => setShowDeleteCompleted(false)} />
        </>);
};
